import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from "react-router-dom";
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

const parseBooleanEnv = (value, defaultValue = false) => {
  if (value === undefined || value === null || value === '') {
    return defaultValue;
  }

  return ['1', 'true', 'yes', 'on'].includes(String(value).trim().toLowerCase());
};

const buildId = process.env.REACT_APP_BUILD_ID || 'dev-local';
const publicUrl = process.env.PUBLIC_URL || '';
const isPwaEnabled = parseBooleanEnv(process.env.REACT_APP_ENABLE_PWA, false);

const resolvePublicAsset = (assetPath) => {
  const normalizedBase = String(publicUrl || '').replace(/\/$/, '');
  const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
  return `${normalizedBase}${normalizedPath}`;
};

const syncManifestLink = () => {
  const existingLinks = Array.from(document.querySelectorAll('link[rel="manifest"]'));

  if (!isPwaEnabled) {
    existingLinks.forEach((link) => link.remove());
    return;
  }

  const manifestHref = `${resolvePublicAsset('/manifest.json')}?v=${encodeURIComponent(buildId)}`;
  const manifestLink = existingLinks[0] || document.createElement('link');
  manifestLink.setAttribute('rel', 'manifest');
  manifestLink.setAttribute('href', manifestHref);

  if (!existingLinks.length) {
    document.head.appendChild(manifestLink);
  }

  existingLinks.slice(1).forEach((link) => link.remove());
};

const clearLegacyPwaArtifacts = async () => {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister().catch(() => false)));
  }

  if ('caches' in window) {
    const cacheKeys = await caches.keys();
    await Promise.all(
      cacheKeys
        .filter((key) => key.startsWith('netbilling-shell-'))
        .map((key) => caches.delete(key))
    );
  }
};

syncManifestLink();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>
);

reportWebVitals();

if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    (async () => {
      if (!isPwaEnabled) {
        await clearLegacyPwaArtifacts();
        return;
      }

      const candidateUrl = `${resolvePublicAsset('/sw.js')}?build=${encodeURIComponent(buildId)}`;
      const fallbackUrl = resolvePublicAsset('/sw.js');

      async function tryRegister(url) {
        try {
          const registration = await navigator.serviceWorker.register(url, { updateViaCache: 'none' });
          if (registration && typeof registration.update === 'function') {
            registration.update();
          }
          return registration;
        } catch (e) {
          throw e;
        }
      }

      try {
        // Check Content-Type first to avoid "unsupported MIME type ('text/html')" errors
        const resp = await fetch(candidateUrl, { cache: 'no-store' });
        const ct = resp.headers.get('content-type') || '';
        if (resp.ok && /javascript/.test(ct)) {
          await tryRegister(candidateUrl);
          return;
        }

        // Fallback to plain /sw.js
        const resp2 = await fetch(fallbackUrl, { cache: 'no-store' });
        const ct2 = resp2.headers.get('content-type') || '';
        if (resp2.ok && /javascript/.test(ct2)) {
          await tryRegister(fallbackUrl);
          return;
        }

        console.warn('Service worker file not found or wrong content-type:', candidateUrl, ct, resp.status, fallbackUrl, ct2, resp2.status);
      } catch (error) {
        console.error('Service worker registration failed:', error);
        // Final attempt: try fallback register without prior fetch
        try {
          await tryRegister(fallbackUrl);
        } catch (err2) {
          console.error('Service worker fallback registration failed:', err2);
        }
      }
    })();
  });
}
